<template>
  <!-- 添加全局背景包装器 -->
  <GlobalBackgroundWrapper :page-key="'schedule'">
    <div class="schedule-container">
      <!-- 页面头部 -->
      <div class="settings-header">
        <div class="header-content">
          <i class="el-icon-date header-icon"></i>
          <div class="header-text">
            <h2 class="page-title">排班管理</h2>
            <p class="page-subtitle">管理教练排班和预约时段设置</p>
          </div>
          <div class="header-actions">
            <el-button
                type="text"
                @click="refreshAllData"
                :loading="refreshing"
                icon="el-icon-refresh"
                class="refresh-btn"
                :title="refreshing ? '刷新中...' : '刷新数据'"
            >
              {{ refreshing ? '刷新中...' : '刷新' }}
            </el-button>
          </div>
        </div>
      </div>

      <el-card v-loading="loading.load" class="settings-card">
        <!-- 移动端标签选择器 -->
        <div class="mobile-tab-selector" v-if="isMobile">
          <el-select
              v-model="activeTab"
              placeholder="请选择功能"
              class="mobile-tab-select"
              size="large"
          >
            <el-option label="排班设置" value="schedule"></el-option>
            <el-option label="排班列表" value="list"></el-option>
            <el-option label="日历视图" value="calendar"></el-option>
          </el-select>
        </div>

        <!-- 桌面端标签页 -->
        <el-tabs v-model="activeTab" v-else class="desktop-tabs">
          <el-tab-pane label="排班设置" name="schedule"></el-tab-pane>
          <el-tab-pane label="排班列表" name="list"></el-tab-pane>
          <el-tab-pane label="日历视图" name="calendar"></el-tab-pane>
        </el-tabs>

        <div class="tab-content">
          <!-- 排班设置 -->
          <div v-if="activeTab === 'schedule'" class="settings-section">
            <div class="section-header">
              <i class="el-icon-date section-icon"></i>
              <h3 class="section-title">排班设置</h3>
              <p class="section-description">设置指定日期的可预约时段和最大同时训练人数</p>
            </div>

            <div class="date-selector-container">
              <div class="date-selector">
                <span class="date-label">选择日期：</span>
                <el-date-picker
                    v-model="selectedDate"
                    type="date"
                    placeholder="选择日期"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    @change="loadSchedule"
                    :picker-options="datePickerOptions"
                    size="large"
                    class="date-picker"
                ></el-date-picker>
                <el-button
                    type="text"
                    @click="goToday"
                    icon="el-icon-s-home"
                    class="today-btn"
                >
                  今天
                </el-button>
              </div>
            </div>

            <el-form :model="scheduleForm" ref="form" :label-position="isMobile ? 'top' : 'left'" class="settings-form">
              <div class="settings-grid">
                <!-- 最大同时训练人数设置 -->
                <div class="setting-item-card max-students-section">
                  <div class="setting-content-wrapper">
                    <div class="setting-header">
                      <span class="setting-label">最大同时训练人数</span>
                      <el-tooltip
                          content="每个时段最多可同时训练的学员数。设置后，学员预约时每个时段最多只能有指定数量的学员同时训练。"
                          placement="top"
                          effect="light"
                      >
                        <i class="el-icon-question help-icon"></i>
                      </el-tooltip>
                    </div>
                    <div class="setting-control">
                      <div class="max-students-control">
                        <el-input-number
                            v-model="scheduleForm.maxStudents"
                            :min="1"
                            :max="10"
                            :step="1"
                            size="large"
                            class="max-students-input"
                            controls-position="right"
                        >
                          <template #append>人</template>
                        </el-input-number>
                        <div class="input-hint">
                          每个时段最多可同时训练 {{ scheduleForm.maxStudents }} 名学员
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="setting-item-card time-slots-section">
                  <div class="setting-content-wrapper">
                    <div class="setting-header">
                      <span class="setting-label">可预约时段</span>
                      <el-tooltip
                          content="点击选择或取消选择可预约的时段。已设置的时段将在学员端显示为可预约。"
                          placement="top"
                          effect="light"
                      >
                        <i class="el-icon-question help-icon"></i>
                      </el-tooltip>
                    </div>
                    <div class="setting-control">
                      <div class="time-slots-container">
                        <div
                            v-for="slot in timeSlotsOptions"
                            :key="slot.value"
                            class="time-slot-item"
                            :class="{ 'active': scheduleForm.timeSlots.includes(slot.value) }"
                            @click="toggleTimeSlot(slot.value)"
                        >
                          <div class="slot-content">
                            <div class="slot-time">{{ slot.label }}</div>
                            <div class="slot-status">
                              <i class="el-icon-check" v-if="scheduleForm.timeSlots.includes(slot.value)"></i>
                              <i class="el-icon-close" v-else></i>
                            </div>
                          </div>
                          <div class="slot-hint" v-if="scheduleForm.timeSlots.includes(slot.value)">
                            最多{{ scheduleForm.maxStudents }}人
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="setting-item-card">
                  <div class="setting-content-wrapper">
                    <div class="setting-header">
                      <span class="setting-label">备注信息</span>
                      <el-tooltip
                          content="填写不能排班的原因或其他备注信息"
                          placement="top"
                          effect="light"
                      >
                        <i class="el-icon-question help-icon"></i>
                      </el-tooltip>
                    </div>
                    <div class="setting-control">
                      <el-input
                          v-model="scheduleForm.remark"
                          type="textarea"
                          rows="3"
                          placeholder="填写不能排班的原因或其他备注信息（可选）"
                          maxlength="200"
                          show-word-limit
                          resize="none"
                          class="remark-textarea"
                      ></el-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 移动端底部固定按钮 -->
              <div class="form-actions" :class="{ 'mobile-actions': isMobile }">
                <el-button
                    type="primary"
                    @click="saveSchedule"
                    :loading="loading.save"
                    class="save-btn primary-btn"
                    :size="isMobile ? 'medium' : 'large'"
                >
                  <i class="el-icon-check"></i>
                  保存排班
                </el-button>
                <el-button
                    @click="clearSchedule"
                    :loading="loading.clear"
                    class="secondary-btn"
                    :size="isMobile ? 'medium' : 'large'"
                >
                  <i class="el-icon-delete"></i>
                  清除排班
                </el-button>
                <el-button
                    @click="resetForm"
                    class="reset-btn"
                    :size="isMobile ? 'medium' : 'large'"
                >
                  <i class="el-icon-refresh"></i>
                  重置表单
                </el-button>
              </div>
            </el-form>
          </div>

          <!-- 排班列表 -->
          <div v-if="activeTab === 'list'" class="settings-section">
            <div class="section-header">
              <i class="el-icon-tickets section-icon"></i>
              <h3 class="section-title">排班列表</h3>
              <p class="section-description">查看和管理历史排班记录</p>
            </div>

            <div class="search-section">
              <el-card shadow="hover" class="search-card">
                <div class="search-content">
                  <!-- 搜索标题区域 -->
                  <div class="search-header">
                    <div class="search-title">
                      <i class="el-icon-search search-title-icon"></i>
                      <span class="search-title-text">排班搜索</span>
                    </div>
                    <div class="search-tips">
                      <el-tag size="small" type="info" effect="plain">
                        <i class="el-icon-info"></i>
                        支持按日期范围查询
                      </el-tag>
                    </div>
                  </div>

                  <!-- 搜索表单区域 -->
                  <div class="search-form">
                    <!-- 日期范围选择 -->
                    <div class="date-range-group">
                      <div class="date-range-item">
                        <div class="date-range-label">
                          <i class="el-icon-date label-icon"></i>
                          <span>开始日期</span>
                        </div>
                        <el-date-picker
                            v-model="searchForm.startDate"
                            type="date"
                            placeholder="请选择开始日期"
                            format="yyyy-MM-dd"
                            value-format="yyyy-MM-dd"
                            size="medium"
                            class="search-date-picker"
                            :picker-options="{
              disabledDate(time) {
                return time.getTime() > Date.now();
              }
            }"
                        ></el-date-picker>
                      </div>

                      <div class="date-range-divider">
                        <i class="el-icon-right"></i>
                      </div>

                      <div class="date-range-item">
                        <div class="date-range-label">
                          <i class="el-icon-date label-icon"></i>
                          <span>结束日期</span>
                        </div>
                        <el-date-picker
                            v-model="searchForm.endDate"
                            type="date"
                            placeholder="请选择结束日期"
                            format="yyyy-MM-dd"
                            value-format="yyyy-MM-dd"
                            size="medium"
                            class="search-date-picker"
                            :picker-options="{
              disabledDate(time) {
                return time.getTime() > Date.now();
              }
            }"
                        ></el-date-picker>
                      </div>
                    </div>

                    <!-- 操作按钮区域 -->
                    <div class="search-buttons">
                      <el-button
                          type="primary"
                          @click="handleSearch"
                          :loading="loading.search"
                          class="search-btn"
                          size="medium"
                          :class="{ 'wide-btn': isMobile }"
                      >
                        <i class="el-icon-search"></i>
                        <span class="btn-text">搜索排班</span>
                      </el-button>

                      <el-button
                          @click="resetSearch"
                          class="reset-btn"
                          size="medium"
                          :class="{ 'wide-btn': isMobile }"
                      >
                        <i class="el-icon-refresh-right"></i>
                        <span class="btn-text">重置条件</span>
                      </el-button>

                      <el-button
                          type="text"
                          @click="refreshList"
                          class="refresh-text-btn"
                          size="medium"
                      >
                        <i class="el-icon-refresh"></i>
                        <span>刷新列表</span>
                      </el-button>
                    </div>

                    <!-- 搜索状态提示 -->
                    <div class="search-status" v-if="searchForm.startDate || searchForm.endDate">
                      <el-tag size="small" type="success" effect="light" class="status-tag">
                        <i class="el-icon-time"></i>
                        <span>搜索条件：</span>
                        <span class="status-value">
            {{ searchForm.startDate || '不限' }}
            <i class="el-icon-right"></i>
            {{ searchForm.endDate || '不限' }}
          </span>
                      </el-tag>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>

            <div class="list-section">
              <el-card shadow="hover" class="list-card">
                <div class="list-header">
                  <div class="list-title">
                    <i class="el-icon-document"></i>
                    排班记录
                  </div>
                  <div class="list-subtitle">
                    <span class="total-count">共 {{ scheduleList.length }} 条记录</span>
                  </div>
                </div>

                <!-- 桌面端表格 -->
                <el-table
                    :data="scheduleList"
                    v-loading="loading.list"
                    style="width: 100%"
                    :default-sort="{prop: 'scheduleDate', order: 'descending'}"
                    class="schedule-table"
                    stripe
                    v-if="!isMobile"
                >
                  <el-table-column prop="scheduleDate" label="日期" sortable width="120" align="center">
                    <template slot-scope="{row}">
                      <div class="date-cell">
                        <div class="date-display">{{ formatDisplayDate(row.scheduleDate) }}</div>
                        <div
                            class="weekday"
                            :class="{'weekend': isWeekend(row.scheduleDate)}"
                        >
                          {{ getWeekday(row.scheduleDate) }}
                        </div>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column prop="timeSlots" label="排班时段" min-width="200">
                    <template slot-scope="{row}">
                      <div class="time-slots-display">
                        <el-tag
                            v-for="(slot, index) in row.timeSlots"
                            :key="index"
                            size="small"
                            type="success"
                            class="time-slot-tag"
                        >
                          {{ slot }}
                        </el-tag>
                        <span v-if="!row.timeSlots || row.timeSlots.length === 0" class="no-schedule">
                        未排班
                      </span>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column prop="maxStudents" label="最大人数" width="100" align="center">
                    <template slot-scope="{row}">
                      <el-tag size="small" type="info" effect="plain">
                        {{ row.maxStudents || 1 }}人
                      </el-tag>
                    </template>
                  </el-table-column>

                  <el-table-column prop="remark" label="备注" show-overflow-tooltip min-width="150">
                    <template slot-scope="{row}">
                      <span :class="{'no-remark': !row.remark}">
                      {{ row.remark || '无备注' }}
                    </span>
                    </template>
                  </el-table-column>

                  <el-table-column label="操作" width="150" fixed="right" align="center">
                    <template slot-scope="{row}">
                      <el-button
                          size="mini"
                          type="primary"
                          @click="editSchedule(row)"
                          icon="el-icon-edit"
                          circle
                      ></el-button>
                      <el-button
                          size="mini"
                          type="danger"
                          @click="deleteSchedule(row)"
                          icon="el-icon-delete"
                          circle
                      ></el-button>
                    </template>
                  </el-table-column>
                </el-table>

                <!-- 移动端列表 -->
                <div v-if="isMobile" class="mobile-schedule-list" v-loading="loading.list">
                  <div v-for="schedule in scheduleList" :key="schedule.scheduleDate" class="schedule-item-card">
                    <el-card shadow="hover">
                      <div class="schedule-header">
                        <div class="schedule-date">
                          <div class="date-display">{{ formatDisplayDate(schedule.scheduleDate) }}</div>
                          <div class="weekday" :class="{'weekend': isWeekend(schedule.scheduleDate)}">
                            {{ getWeekday(schedule.scheduleDate) }}
                          </div>
                        </div>
                        <div class="schedule-actions">
                          <el-button
                              size="mini"
                              type="primary"
                              @click="editSchedule(schedule)"
                              icon="el-icon-edit"
                              circle
                          ></el-button>
                          <el-button
                              size="mini"
                              type="danger"
                              @click="deleteSchedule(schedule)"
                              icon="el-icon-delete"
                              circle
                          ></el-button>
                        </div>
                      </div>

                      <div class="schedule-content">
                        <div class="max-students-section" v-if="schedule.maxStudents">
                          <div class="section-label">最大人数:</div>
                          <div class="max-students-display">
                            <el-tag size="small" type="info" effect="plain">
                              {{ schedule.maxStudents }}人
                            </el-tag>
                          </div>
                        </div>

                        <div class="time-slots-section">
                          <div class="section-label">排班时段:</div>
                          <div class="time-slots-display">
                            <el-tag
                                v-for="(slot, index) in schedule.timeSlots"
                                :key="index"
                                size="small"
                                type="success"
                                class="time-slot-tag"
                            >
                              {{ slot }}
                            </el-tag>
                            <span v-if="!schedule.timeSlots || schedule.timeSlots.length === 0" class="no-schedule">
                            未排班
                          </span>
                          </div>
                        </div>

                        <div class="remark-section" v-if="schedule.remark">
                          <div class="section-label">备注:</div>
                          <div class="remark-content">{{ schedule.remark }}</div>
                        </div>
                      </div>
                    </el-card>
                  </div>
                </div>
              </el-card>
            </div>
          </div>

          <!-- 日历视图 -->
          <div v-if="activeTab === 'calendar'" class="settings-section">
            <div class="section-header">
              <i class="el-icon-data-analysis section-icon"></i>
              <h3 class="section-title">日历视图</h3>
              <p class="section-description">月度排班概览和快速操作</p>
            </div>

            <div class="calendar-section">
              <el-card shadow="hover" class="calendar-card">
                <div class="calendar-header">
                  <div class="calendar-title">
                    {{ calendarDate.getFullYear() }}年{{ calendarDate.getMonth() + 1 }}月排班概览
                  </div>
                  <div class="calendar-controls">
                    <el-button-group>
                      <el-button @click="prevMonth" icon="el-icon-arrow-left" size="small">上月</el-button>
                      <el-button @click="nextMonth" icon="el-icon-arrow-right" size="small">下月</el-button>
                    </el-button-group>
                    <el-button @click="goToday" size="small" class="today-btn">今天</el-button>
                  </div>
                </div>

                <el-calendar v-model="calendarDate" class="custom-calendar">
                  <template slot="dateCell" slot-scope="{date, data}">
                    <div
                        class="calendar-cell"
                        :class="{
                      'has-schedule': hasSchedule(date),
                      'selected': isSelectedDate(date),
                      'today': isToday(date)
                    }"
                        @click="selectCalendarDate(date)"
                    >
                      <div class="cell-date">{{ data.day.split('-').slice(2).join('-') }}</div>
                      <div class="cell-indicator">
                        <i class="el-icon-check" v-if="hasSchedule(date)"></i>
                        <i class="el-icon-close" v-else></i>
                      </div>
                      <div class="cell-max-students" v-if="getMaxStudents(date)">
                        {{ getMaxStudents(date) }}人
                      </div>
                    </div>
                  </template>
                </el-calendar>
              </el-card>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </GlobalBackgroundWrapper>
</template>

<script>
import { scheduleAPI } from '@/services/api'
import GlobalBackgroundWrapper from '@/components/GlobalBackgroundWrapper.vue'

export default {
  name: 'ScheDule',
  components: {
    GlobalBackgroundWrapper
  },
  data() {
    return {
      activeTab: 'schedule',
      selectedDate: new Date().toISOString().split('T')[0],
      calendarDate: new Date(),
      scheduleForm: {
        timeSlots: [],
        remark: '',
        maxStudents: 1  // 新增：最大同时训练人数
      },
      searchForm: {
        startDate: '',
        endDate: ''
      },
      scheduleList: [],
      monthlySchedules: [],
      refreshing: false,
      loading: {
        save: false,
        clear: false,
        search: false,
        list: false,
        load: false
      },
      isMobile: false,
      timeSlotsOptions: [
        { value: '08:00-10:00', label: '08:00-10:00' },
        { value: '10:00-12:00', label: '10:00-12:00' },
        { value: '14:00-16:00', label: '14:00-16:00' },
        { value: '16:00-18:00', label: '16:00-18:00' },
        { value: '18:00-20:00', label: '18:00-20:00' }
      ],
      datePickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7
        }
      }
    }
  },
  mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    this.loadSchedule()
    this.loadMonthlySchedule()
    this.loadAllSchedules()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    // 响应式消息提示
    showMessage(message, type = 'info') {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type,
        position,
        duration: 3000,
        showClose: true
      })
    },

    // 响应式警告提示
    showWarning(message) {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type: 'warning',
        position,
        duration: 3000,
        showClose: true
      })
    },

    // 响应式确认框
    showConfirm(message, title, options = {}) {
      const defaultOptions = {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: this.isMobile ? 'mobile-confirm-dialog' : ''
      }

      return this.$confirm(message, title, {
        ...defaultOptions,
        ...options
      })
    },

    // 新增刷新方法
    async refreshAllData() {
      this.refreshing = true
      try {
        // 并行加载所有数据
        await Promise.all([
          this.loadSchedule(),
          this.loadMonthlySchedule(),
          this.loadAllSchedules()
        ])
        this.showMessage('数据刷新成功', 'success')
      } catch (error) {
        console.error('刷新数据失败:', error)
        this.showMessage('刷新数据失败', 'error')
      } finally {
        this.refreshing = false
      }
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    // 加载指定日期的排班
    async loadSchedule(date = this.selectedDate) {
      if (!date) return
      try {
        const res = await scheduleAPI.getSchedule({date: date})
        if (res.data && res.data.timeSlots) {
          this.scheduleForm = {
            timeSlots: res.data.timeSlots,
            remark: res.data.remark || '',
            maxStudents: res.data.maxStudents || 1  // 新增：加载最大人数
          }
        } else {
          this.scheduleForm = {timeSlots: [], remark: '', maxStudents: 1}
        }
      } catch (error) {
        console.error('加载排班失败:', error)
        this.showMessage('加载排班失败', 'error')
        this.scheduleForm = {timeSlots: [], remark: '', maxStudents: 1}
      }
    },

    // 保存排班（包含最大人数）
    async saveSchedule() {
      if (!this.selectedDate) {
        this.showWarning('请选择日期')
        return
      }

      this.loading.save = true
      try {
        const data = {
          date: this.selectedDate,
          timeSlots: this.scheduleForm.timeSlots,
          remark: this.scheduleForm.remark,
          maxStudents: this.scheduleForm.maxStudents  // 新增：最大人数
        }

        await scheduleAPI.saveSchedule(data)
        this.showMessage('排班保存成功', 'success')
        // 刷新相关数据
        this.loadMonthlySchedule()
        this.loadAllSchedules()
      } catch (error) {
        this.showMessage(error.response?.data?.message || '保存失败', 'error')
      } finally {
        this.loading.save = false
      }
    },

    // 清除排班
    async clearSchedule() {
      if (!this.selectedDate) {
        this.showWarning('请选择日期')
        return
      }

      try {
        await this.showConfirm('确定要清除该日期的排班吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        this.loading.clear = true
        await scheduleAPI.deleteSchedule({date: this.selectedDate})
        this.showMessage('排班清除成功', 'success')
        this.scheduleForm = {timeSlots: [], remark: '', maxStudents: 1}
        // 刷新相关数据
        this.loadMonthlySchedule()
        this.loadAllSchedules()
      } catch (error) {
        if (error !== 'cancel') {
          this.showMessage(error.response?.data?.message || '清除失败', 'error')
        }
      } finally {
        this.loading.clear = false
      }
    },

    // 加载所有排班
    async loadAllSchedules() {
      this.loading.list = true
      try {
        const res = await scheduleAPI.getAllSchedules()
        this.scheduleList = res.data || []
      } catch (error) {
        console.error('加载排班列表失败:', error)
        this.showMessage('加载排班列表失败', 'error')
      } finally {
        this.loading.list = false
      }
    },

    // 搜索排班
    async handleSearch() {
      if (!this.searchForm.startDate || !this.searchForm.endDate) {
        this.showWarning('请选择开始日期和结束日期')
        return
      }

      this.loading.search = true
      try {
        const res = await scheduleAPI.searchSchedules({
          startDate: this.searchForm.startDate,
          endDate: this.searchForm.endDate
        })
        this.scheduleList = res.data || []
        this.showMessage(`找到 ${this.scheduleList.length} 条排班记录`, 'success')
      } catch (error) {
        console.error('搜索排班失败:', error)
        this.showMessage('搜索排班失败', 'error')
      } finally {
        this.loading.search = false
      }
    },

    // 编辑排班
    async editSchedule(schedule) {
      this.selectedDate = schedule.scheduleDate
      this.activeTab = 'schedule'

      // 等待排班数据加载完成
      await this.loadSchedule()

      this.showMessage(`已切换到 ${this.selectedDate} 的排班设置`, 'success')
    },

    // 删除排班
    async deleteSchedule(schedule) {
      try {
        await this.showConfirm('确定要删除这条排班记录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await scheduleAPI.deleteSchedule({date: schedule.scheduleDate})
        this.showMessage('排班删除成功', 'success')
        // 刷新列表
        this.loadAllSchedules()
        this.loadMonthlySchedule()
      } catch (error) {
        if (error !== 'cancel') {
          this.showMessage('删除失败', 'error')
        }
      }
    },

    // 重置表单
    resetForm() {
      this.scheduleForm = {timeSlots: [], remark: '', maxStudents: 1}
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {startDate: '', endDate: ''}
      this.loadAllSchedules()
    },

    // 刷新列表
    refreshList() {
      this.loadAllSchedules()
      this.loadMonthlySchedule()
    },

    // 日历相关方法
    async loadMonthlySchedule() {
      try {
        const res = await scheduleAPI.getMonthlySchedule({
          year: this.calendarDate.getFullYear(),
          month: this.calendarDate.getMonth() + 1
        })
        this.monthlySchedules = res.data || []
      } catch (error) {
        console.error('加载月度排班失败:', error)
      }
    },

    hasSchedule(date) {
      const dateStr = this.formatDate(date)
      const schedule = this.monthlySchedules.find(s => s.scheduleDate === dateStr)
      return schedule && schedule.timeSlots && schedule.timeSlots.length > 0
    },

    getMaxStudents(date) {
      const dateStr = this.formatDate(date)
      const schedule = this.monthlySchedules.find(s => s.scheduleDate === dateStr)
      return schedule ? (schedule.maxStudents || 1) : null
    },

    isSelectedDate(date) {
      return this.formatDate(date) === this.selectedDate
    },

    isToday(date) {
      const today = new Date()
      return this.formatDate(date) === this.formatDate(today)
    },

    selectCalendarDate(date) {
      this.selectedDate = this.formatDate(date)
      this.activeTab = 'schedule'
      this.loadSchedule()
    },

    prevMonth() {
      const newDate = new Date(this.calendarDate)
      newDate.setMonth(newDate.getMonth() - 1)
      this.calendarDate = newDate
    },

    nextMonth() {
      const newDate = new Date(this.calendarDate)
      newDate.setMonth(newDate.getMonth() + 1)
      this.calendarDate = newDate
    },

    goToday() {
      this.calendarDate = new Date()
      this.selectedDate = this.formatDate(new Date())
      this.loadSchedule()
    },

    // 切换时间段选择
    toggleTimeSlot(slot) {
      if (this.scheduleForm.timeSlots.includes(slot)) {
        this.scheduleForm.timeSlots = this.scheduleForm.timeSlots.filter(s => s !== slot)
      } else {
        this.scheduleForm.timeSlots.push(slot)
      }
    },

    // 获取星期几
    getWeekday(dateStr) {
      const date = new Date(dateStr)
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return weekdays[date.getDay()]
    },

    // 判断是否为周末
    isWeekend(dateStr) {
      const date = new Date(dateStr)
      const day = date.getDay()
      return day === 0 || day === 6
    },

    // 工具方法
    formatDate(date) {
      return new Date(date).toISOString().split('T')[0]
    },

    formatDisplayDate(dateStr) {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}月${date.getDate()}日`
    }
  },
  watch: {
    calendarDate() {
      this.loadMonthlySchedule()
    }
  }
}
</script>

<style scoped>
.schedule-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, rgba(245, 247, 250, 0.6) 0%, #e4e7ed 100%);
  min-height: 100vh;
}

/* 页面头部 */
.settings-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-icon {
  font-size: 32px;
  color: #409EFF;
  padding: 12px;
  background: linear-gradient(135deg, #ecf5ff, #d9ecff);
  border-radius: 50%;
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1d76d1, #67C23A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-subtitle {
  font-size: 16px;
  color: #606266;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
}

.refresh-btn {
  font-size: 16px;
  color: #409EFF;
}

/* 主卡片 */
.settings-card {
  border-radius: 16px;
  border: none;
  background: rgba(255, 255, 255, 0.0);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* 移动端标签选择器 */
.mobile-tab-selector {
  padding: 20px 20px 0;
}

.mobile-tab-select {
  width: 100%;
}

.mobile-tab-select ::v-deep .el-input__inner {
  height: 52px;
  border-radius: 12px;
  font-size: 16px;
  border: 2px solid rgba(228, 231, 237, 0.6);
}

/* 桌面端标签页 */
.desktop-tabs {
  padding: 0 20px;
}

.desktop-tabs ::v-deep .el-tabs__header {
  margin-bottom: 0;
}

.desktop-tabs ::v-deep .el-tabs__nav-wrap::after {
  height: 1px;
}

.desktop-tabs ::v-deep .el-tabs__item {
  font-size: 16px;
  font-weight: 500;
  padding: 0 24px;
  height: 60px;
}

.desktop-tabs ::v-deep .el-tabs__item.is-active {
  color: #409EFF;
  font-weight: 600;
}

.desktop-tabs ::v-deep .el-tabs__active-bar {
  background-color: #409EFF;
  height: 3px;
  border-radius: 2px;
}

/* 标签内容 */
.tab-content {
  padding: 0;
}

.settings-section {
  padding: 0 20px 20px;
}

/* 区域头部 */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0 20px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 24px;
}

.section-icon {
  font-size: 24px;
  color: #409EFF;
  padding: 8px;
  background: #ecf5ff;
  border-radius: 8px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.section-description {
  font-size: 14px;
  color: #909399;
  margin: 0 0 0 auto;
}

/* 日期选择器 */
.date-selector-container {
  margin-bottom: 24px;
  /* 添加白色背景 + 0.4 透明度 */
  background-color: rgba(255, 255, 255, 0.1);
}

.date-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(248, 249, 250, 0);
  border-radius: 8px;
  border: 1px solid rgba(248, 249, 250, 0);
}

.date-label {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}

.date-picker {
  flex: 1;
  max-width: 200px;
}

.today-btn {
  color: #409EFF;
}

/* 设置表单 */
.settings-form {
  margin-top: 0;
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 32px;
}

/* 设置项卡片 */
.setting-item-card {
  background: rgba(248, 249, 250, 0);
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
}

.setting-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-item-card:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.1);
  transform: translateY(-2px);
}

.setting-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
}

.setting-label {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  flex: 1;
  margin-right: 8px;
}

.help-icon {
  color: #909399;
  cursor: help;
  font-size: 16px;
  transition: color 0.3s ease;
  flex-shrink: 0;
}

.help-icon:hover {
  color: #409EFF;
}

.setting-control {
  width: 100%;
}

/* 时间段选择样式 */
.time-slots-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.time-slot-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: rgba(250, 250, 250, 0.6);
}

.time-slot-item:hover {
  border-color: #c0c4cc;
  background-color: #f0f2f5;
}

.time-slot-item.active {
  border-color: #409EFF;
  background-color: #ecf5ff;
}

.slot-time {
  font-size: 14px;
  color: #606266;
}

.time-slot-item.active .slot-time {
  color: #409EFF;
  font-weight: 500;
}

.slot-status {
  color: #c0c4cc;
}

.time-slot-item.active .slot-status {
  color: #67c23a;
}

/* 备注文本框 */
.remark-textarea ::v-deep .el-textarea__inner {
  border-radius: 8px;
  border: 2px solid #e4e7ed;
  font-size: 14px;
  resize: none;
}

.remark-textarea ::v-deep .el-textarea__inner:focus {
  border-color: #409EFF;
}

/* 操作按钮 */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #ebeef5;
}

.save-btn, .search-btn {
  height: 48px;
  padding: 0 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.primary-btn {
  background: linear-gradient(135deg, #409EFF, #337ecc);
  border: none;
}

.primary-btn:hover {
  background: linear-gradient(135deg, #66b1ff, #409EFF);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.secondary-btn {
  height: 48px;
  padding: 0 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  background: white;
  color: #606266;
}

.secondary-btn:hover {
  border-color: #409EFF;
  color: #409EFF;
  transform: translateY(-1px);
}

.reset-btn {
  height: 48px;
  padding: 0 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #e6a23c;
  background: white;
  color: #e6a23c;
}

.reset-btn:hover {
  background: #fdf6ec;
  transform: translateY(-1px);
}

/* 搜索区域 */
.search-section {
  margin-bottom: 24px;
}

.search-card {
  border-radius: 12px;
  border: none;
}

.search-content {
  padding: 20px;
}

.search-fields {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.field-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.search-date-picker {
  flex: 1;
}

.search-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* 列表区域 */
.list-card {
  border-radius: 12px;
  border: none;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.list-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.list-title i {
  margin-right: 8px;
  color: #409EFF;
}

.list-subtitle {
  font-size: 14px;
  color: #909399;
}

.total-count {
  font-size: 13px;
  color: #909399;
}

/* 表格样式 */
.schedule-table {
  margin-top: 0;
}

.schedule-table ::v-deep .el-table__header {
  background: #f5f7fa;
}

.date-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.date-display {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.weekday {
  font-size: 12px;
  color: #909399;
}

.weekday.weekend {
  color: #f56c6c;
}

.time-slots-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.time-slot-tag {
  margin: 1px;
}

.no-schedule {
  color: #c0c4cc;
  font-size: 13px;
  font-style: italic;
}

.no-remark {
  color: #c0c4cc;
  font-style: italic;
}

/* 移动端排班列表样式 */
.mobile-schedule-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.schedule-item-card {
  width: 100%;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.schedule-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule-actions {
  display: flex;
  gap: 8px;
}

.schedule-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 6px;
}

.remark-content {
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
}

/* 日历区域 */
.calendar-section {
  margin-top: 0;
}

.calendar-card {
  border-radius: 12px;
  border: none;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.calendar-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.custom-calendar {
  border: none;
}

.calendar-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
  padding: 4px;
}

.calendar-cell:hover {
  background-color: #f0f2f5;
}

.calendar-cell.selected {
  background-color: #ecf5ff;
  border: 1px solid #409EFF;
}

.calendar-cell.today {
  background-color: #fdf6ec;
}

.calendar-cell.has-schedule {
  border-left: 3px solid #67c23a;
}

.cell-date {
  font-size: 14px;
  margin-bottom: 2px;
}

.cell-indicator {
  font-size: 12px;
  color: #c0c4cc;
}

.calendar-cell.has-schedule .cell-indicator {
  color: #67c23a;
}

/* 移动端底部固定按钮 */
.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  border-top: 1px solid #ebeef5;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.mobile-actions .el-button {
  flex: 1;
  height: 44px;
  font-size: 14px;
  margin: 0;
}

/* 响应式设计 - 移动端 */
@media (max-width: 768px) {
  .schedule-container {
    padding: 8px;
  }

  /* 移动端头部优化 */
  .settings-header {
    margin-bottom: 16px;
  }

  .header-content {
    padding: 12px 16px;
    gap: 12px;
  }

  .header-icon {
    font-size: 24px;
    padding: 8px;
  }

  .page-title {
    font-size: 20px;
    margin-bottom: 4px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .refresh-btn {
    font-size: 14px;
  }

  .settings-section {
    padding: 0 12px 16px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    padding: 12px 0 16px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-description {
    font-size: 13px;
    margin: 0;
  }

  .date-selector {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;
  }

  .date-label {
    font-size: 14px;
  }

  .date-picker {
    max-width: 100%;
  }

  .today-btn {
    align-self: flex-end;
    font-size: 13px;
  }

  .settings-grid {
    gap: 12px;
  }

  .setting-item-card {
    padding: 16px;
  }

  .setting-content-wrapper {
    gap: 12px;
  }

  .setting-label {
    font-size: 14px;
  }

  .time-slots-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .time-slot-item {
    padding: 10px 12px;
  }

  .slot-time {
    font-size: 13px;
  }

  .remark-textarea ::v-deep .el-textarea__inner {
    font-size: 13px;
  }

  .search-fields {
    flex-direction: column;
    gap: 12px;
  }

  .search-field {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .field-label {
    text-align: left;
  }

  .search-actions {
    flex-direction: column;
    gap: 8px;
  }

  /* 移动端表单操作按钮 - 防止溢出 */
  .form-actions {
    flex-direction: row; /* 改为横向排列 */
    gap: 8px; /* 减少间距以适应横向排列 */
    flex-wrap: wrap; /* 允许换行 */
    justify-content: space-between; /* 两端对齐 */
  }

  .save-btn, .secondary-btn, .reset-btn, .search-btn {
    width: calc(33.333% - 5.333px); /* 三个按钮平均分配宽度，减去间隙 */
    height: 52px;
    font-size: 14px; /* 减小字体大小 */
    padding: 0 8px; /* 减少内边距 */
    white-space: nowrap; /* 防止文字换行 */
    overflow: hidden; /* 隐藏溢出内容 */
    text-overflow: ellipsis; /* 文字溢出显示省略号 */
  }

  /* 确保按钮图标和文字不重叠 */
  .save-btn i, .secondary-btn i, .reset-btn i, .search-btn i {
    margin-right: 4px;
    font-size: 12px;
  }

  .list-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .calendar-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .calendar-controls {
    align-self: center;
  }

  .date-display {
    font-size: 15px;
  }

  .section-label {
    font-size: 13px;
  }

  .remark-content {
    font-size: 13px;
  }

  /* 为移动端底部按钮腾出空间 */
  .settings-section {
    padding-bottom: 80px;
  }

  .settings-card {
    margin-bottom: 0;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .time-slots-container {
    grid-template-columns: 1fr;
  }

  .mobile-actions {
    padding: 10px 12px;
  }

  .mobile-actions .el-button {
    height: 42px;
    font-size: 13px;
    padding: 0 6px; /* 进一步减少内边距 */
  }

  .schedule-header {
    flex-direction: column;
    gap: 10px;
  }

  .schedule-actions {
    align-self: flex-end;
  }

  /* 小屏幕下调整按钮内边距，确保文字不换行 */
  .mobile-actions .el-button i {
    margin-right: 4px;
    font-size: 11px;
  }

  /* 超小屏幕下的表单按钮 */
  .save-btn, .secondary-btn, .reset-btn, .search-btn {
    width: calc(33.333% - 5.333px); /* 保持三个按钮等宽 */
    height: 48px; /* 减小高度 */
    font-size: 12px; /* 进一步减小字体 */
    padding: 0 4px; /* 进一步减少内边距 */
  }

  .save-btn i, .secondary-btn i, .reset-btn i, .search-btn i {
    margin-right: 2px;
    font-size: 11px;
  }
}

/* 极端小屏幕优化（如iPhone 5/SE等） */
@media (max-width: 320px) {
  .mobile-actions .el-button {
    font-size: 11px;
    height: 40px;
  }

  .save-btn, .secondary-btn, .reset-btn, .search-btn {
    width: calc(33.333% - 4px); /* 进一步减少间隙 */
    font-size: 11px;
    padding: 0 2px;
  }

  /* 极端情况下隐藏按钮文字，只显示图标 */
  .mobile-actions .el-button span:not(.el-icon) {
    display: none;
  }

  .mobile-actions .el-button i {
    margin-right: 0;
    font-size: 14px;
  }

  /* 或者考虑将三个按钮改为纵向排列 */
  /*
  .mobile-actions {
    flex-direction: column;
    gap: 6px;
  }

  .mobile-actions .el-button {
    width: 100%;
  }
  */
}

/* 加载状态优化 */
::v-deep .el-loading-mask {
  border-radius: 16px;
}

::v-deep .el-card__header {
  padding: 0;
}

::v-deep .el-card__body {
  padding: 0;
}

/* 确保所有父容器不会裁剪内容 */
.schedule-container,
.settings-card,
.tab-content,
.settings-section,
.settings-form,
.settings-grid {
  overflow: visible !important;
}
</style>

<style>
/* 全局样式 - 移动端消息提示和对话框优化 */
@media (max-width: 768px) {
  .el-message {
    min-width: 90% !important;
    width: auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    top: 20px !important;
  }

  .mobile-confirm-dialog {
    width: 90% !important;
    max-width: 320px !important;
    margin: 0 auto !important;
  }

  .mobile-confirm-dialog .el-message-box__content {
    padding: 15px 20px !important;
  }

  .mobile-confirm-dialog .el-message-box__btns {
    padding: 10px 20px 15px !important;
  }
}

/* 桌面端消息提示位置优化 */
@media (min-width: 769px) {
  .el-message {
    min-width: 380px !important;
    top: 20px !important;
    right: 20px !important;
  }
}
</style>
